import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Upload, 
  FileText, 
  Trash2, 
  FolderOpen,
  Search,
  Filter,
  Loader2,
  FileUp,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { getDocuments, uploadDocument, deleteDocument, getPIs } from '@/lib/api';

const DOC_TYPES = [
  { value: 'sponsor_packet', label: 'Sponsor Packet', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'cv', label: 'PI CV', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { value: 'past_feasibility', label: 'Past Feasibility', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

export default function DocumentLibrary() {
  const [documents, setDocuments] = useState([]);
  const [pis, setPIs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  
  const fileInputRef = useRef(null);
  const [uploadData, setUploadData] = useState({
    file: null,
    doc_type: '',
    name: '',
    pi_id: ''
  });

  const loadData = useCallback(async () => {
    try {
      const [docsData, pisData] = await Promise.all([
        getDocuments(),
        getPIs()
      ]);
      setDocuments(docsData);
      setPIs(pisData);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
    // Module-level imports + stable setters only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadData(prev => ({ 
        ...prev, 
        file,
        name: file.name.replace(/\.[^/.]+$/, '')
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file || !uploadData.doc_type) {
      toast.error('Please select a file and document type');
      return;
    }

    setUploading(true);
    try {
      await uploadDocument(
        uploadData.file, 
        uploadData.doc_type, 
        uploadData.pi_id || null,
        uploadData.name || null
      );
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadData({ file: null, doc_type: '', name: '', pi_id: '' });
      await loadData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await deleteDocument(docId);
      toast.success('Document deleted');
      await loadData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getDocTypeInfo = (type) => {
    return DOC_TYPES.find(t => t.value === type) || { label: type, color: 'bg-slate-50 text-slate-700 border-slate-200' };
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || doc.doc_type === filterType;
    return matchesSearch && matchesType;
  });

  const groupedDocuments = {
    sponsor_packet: filteredDocuments.filter(d => d.doc_type === 'sponsor_packet'),
    cv: filteredDocuments.filter(d => d.doc_type === 'cv'),
    past_feasibility: filteredDocuments.filter(d => d.doc_type === 'past_feasibility'),
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']" data-testid="documents-title">
            Document Library
          </h1>
          <p className="text-slate-600 mt-1">Manage sponsor packets, CVs, and past feasibilities</p>
        </div>
        
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2" data-testid="upload-document-btn">
              <Upload className="w-4 h-4" />
              Upload Document
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
              <DialogDescription>
                Upload a PDF document to use as reference for AI responses
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* File Upload */}
              <div 
                className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-[#2D7DD2] transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="document-file-input"
                />
                {uploadData.file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-[#2D7DD2]" />
                    <span className="text-sm font-medium">{uploadData.file.name}</span>
                  </div>
                ) : (
                  <>
                    <FileUp className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Click to select PDF</p>
                  </>
                )}
              </div>

              {/* Document Name */}
              <div className="space-y-2">
                <Label>Document Name</Label>
                <Input
                  placeholder="Enter document name"
                  value={uploadData.name}
                  onChange={(e) => setUploadData(prev => ({ ...prev, name: e.target.value }))}
                  className="focus-visible:ring-[#2D7DD2]"
                  data-testid="document-name-input"
                />
              </div>

              {/* Document Type */}
              <div className="space-y-2">
                <Label>Document Type *</Label>
                <Select
                  value={uploadData.doc_type}
                  onValueChange={(value) => setUploadData(prev => ({ ...prev, doc_type: value }))}
                >
                  <SelectTrigger className="focus:ring-[#2D7DD2]" data-testid="document-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* PI Selection (for CVs) */}
              {uploadData.doc_type === 'cv' && pis.length > 0 && (
                <div className="space-y-2">
                  <Label>Link to PI (optional)</Label>
                  <Select
                    value={uploadData.pi_id}
                    onValueChange={(value) => setUploadData(prev => ({ ...prev, pi_id: value }))}
                  >
                    <SelectTrigger className="focus:ring-[#2D7DD2]">
                      <SelectValue placeholder="Select PI" />
                    </SelectTrigger>
                    <SelectContent>
                      {pis.map((pi) => (
                        <SelectItem key={pi.id} value={pi.id}>
                          {pi.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={uploading || !uploadData.file || !uploadData.doc_type}
                className="btn-primary"
                data-testid="confirm-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 focus-visible:ring-[#2D7DD2]"
            data-testid="search-documents-input"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-48 focus:ring-[#2D7DD2]" data-testid="filter-type-select">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {DOC_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : filteredDocuments.length === 0 ? (
        <Card className="stryde-card">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No documents found</h3>
            <p className="text-slate-500 mb-4">
              {searchQuery || filterType !== 'all' 
                ? 'Try adjusting your search or filter'
                : 'Upload your first document to get started'}
            </p>
            {!searchQuery && filterType === 'all' && (
              <Button onClick={() => setUploadDialogOpen(true)} className="btn-primary">
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="all" className="space-y-4">
          <TabsList>
            <TabsTrigger value="all">All ({filteredDocuments.length})</TabsTrigger>
            <TabsTrigger value="sponsor_packet">
              Sponsor Packets ({groupedDocuments.sponsor_packet.length})
            </TabsTrigger>
            <TabsTrigger value="cv">
              CVs ({groupedDocuments.cv.length})
            </TabsTrigger>
            <TabsTrigger value="past_feasibility">
              Past Feasibilities ({groupedDocuments.past_feasibility.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDocuments.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  typeInfo={getDocTypeInfo(doc.doc_type)}
                  onDelete={handleDelete}
                  onPreview={() => setPreviewDoc(doc)}
                />
              ))}
            </div>
          </TabsContent>

          {Object.entries(groupedDocuments).map(([type, docs]) => (
            <TabsContent key={type} value={type} className="mt-4">
              {docs.length === 0 ? (
                <Card className="stryde-card">
                  <CardContent className="py-12 text-center">
                    <p className="text-slate-500">No {getDocTypeInfo(type).label.toLowerCase()} documents</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {docs.map((doc) => (
                    <DocumentCard
                      key={doc.id}
                      doc={doc}
                      typeInfo={getDocTypeInfo(doc.doc_type)}
                      onDelete={handleDelete}
                      onPreview={() => setPreviewDoc(doc)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Preview Dialog */}
      {previewDoc && (
        <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>{previewDoc.name}</DialogTitle>
              <DialogDescription>
                Extracted text preview
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto max-h-[60vh] p-4 bg-slate-50 rounded-lg">
              {previewDoc.extracted_text ? (
                <pre className="pdf-text text-slate-700 whitespace-pre-wrap">
                  {previewDoc.extracted_text.slice(0, 5000)}
                  {previewDoc.extracted_text.length > 5000 && '\n\n... (truncated)'}
                </pre>
              ) : (
                <p className="text-slate-500 text-center py-8">No text extracted from this document</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function DocumentCard({ doc, typeInfo, onDelete, onPreview }) {
  return (
    <Card className="stryde-card-interactive group" data-testid={`document-card-${doc.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
            doc.doc_type === 'sponsor_packet' ? 'bg-blue-100' :
            doc.doc_type === 'cv' ? 'bg-purple-100' :
            'bg-emerald-100'
          }`}>
            <FileText className={`w-5 h-5 ${
              doc.doc_type === 'sponsor_packet' ? 'text-blue-600' :
              doc.doc_type === 'cv' ? 'text-purple-600' :
              'text-emerald-600'
            }`} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-slate-900 truncate">{doc.name}</h4>
            <Badge className={`mt-1 text-xs ${typeInfo.color}`}>
              {typeInfo.label}
            </Badge>
            {doc.metadata?.size && (
              <p className="text-xs text-slate-400 mt-1">
                {(doc.metadata.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onPreview();
            }}
            data-testid={`preview-doc-${doc.id}`}
          >
            <Eye className="w-4 h-4 mr-1" />
            Preview
          </Button>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={(e) => e.stopPropagation()}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Document</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{doc.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => onDelete(doc.id)}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}
